import { memo, useCallback } from 'react';
import { useStore, type ReactFlowState, type MiniMapNodeProps } from '@xyflow/react';

const CIRCLE_TYPES = new Set(['trigger', 'placeholderTrigger']);
const DIAMOND_TYPES = new Set(['if', 'switch', 'foreach']);
const INVISIBLE_TYPES = new Set(['phantom', 'merge']);
const SCOPE_TYPE = 'scopeContainer';

function MiniMapNodeInner({
  id,
  x,
  y,
  width,
  height,
  color,
  strokeColor,
  strokeWidth,
  selected,
}: MiniMapNodeProps) {
  const nodeType = useStore(
    useCallback(
      (state: ReactFlowState) => state.nodeLookup.get(id)?.type ?? 'action',
      [id]
    )
  );

  // Invisible structural nodes
  if (INVISIBLE_TYPES.has(nodeType)) {
    return null;
  }

  // Scope containers — outline only
  if (nodeType === SCOPE_TYPE) {
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={4}
        ry={4}
        fill="none"
        stroke={color}
        strokeWidth={1}
        opacity={0.3}
      />
    );
  }

  // Circle for triggers
  if (CIRCLE_TYPES.has(nodeType)) {
    const cx = x + width / 2;
    const cy = y + height / 2;
    const r = Math.min(width, height) / 2;
    return (
      <ellipse
        cx={cx}
        cy={cy}
        rx={r}
        ry={r}
        fill={color}
        stroke={selected ? strokeColor : 'none'}
        strokeWidth={selected ? (strokeWidth ?? 2) : 0}
      />
    );
  }

  // Diamond for control flow
  if (DIAMOND_TYPES.has(nodeType)) {
    const cx = x + width / 2;
    const cy = y + height / 2;
    const points = `${cx},${y} ${x + width},${cy} ${cx},${y + height} ${x},${cy}`;
    return (
      <polygon
        points={points}
        fill={color}
        stroke={selected ? strokeColor : 'none'}
        strokeWidth={selected ? (strokeWidth ?? 2) : 0}
      />
    );
  }

  // Note — reduced opacity
  if (nodeType === 'note') {
    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={2}
        ry={2}
        fill={color}
        opacity={0.5}
        stroke={selected ? strokeColor : 'none'}
        strokeWidth={selected ? (strokeWidth ?? 2) : 0}
      />
    );
  }

  // Default: rounded rectangle (action, agent, tool, model)
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      rx={4}
      ry={4}
      fill={color}
      stroke={selected ? strokeColor : 'none'}
      strokeWidth={selected ? (strokeWidth ?? 2) : 0}
    />
  );
}

export const MiniMapNode = memo(MiniMapNodeInner);
